/**
 * Bridge to the legacy core (`public/legacy/core.js`).
 *
 * The core is a classic script, so everything it defines lands on the global
 * object. Module code must not reach for those globals directly — it would
 * couple every new module to the monolith's current shape and make the
 * incremental migration in docs/ARCHITECTURE.md harder to finish. Go through
 * the accessors here instead, so there is exactly one list of what we still
 * depend on.
 */

/** Names the module layer currently relies on. Keep this list shrinking. */
export const LEGACY_GLOBALS = [
  'AppState',
  'handleRoute',
  'showToast',
  'renderMissions',
  'computeUserMissions',
  'supabaseClient',
  'isDemoMode',
  'handleAuthSignOut',
  'checkFraudAndEarnTokens',
  'updateTokenDisplay',
  'sbClaimMission',
  'seedCosmetics',
  // Progression engine: unit display and active-workout set mutation.
  'kgToDisplay',
  'getUnitLabel',
  'updateSetWeight',
  'updateSet',
];

/** Reads a legacy global, or undefined if the core has not defined it. */
export function legacy(name) {
  return globalThis[name];
}

/** True once the legacy core has finished evaluating and exposed its API. */
export function isLegacyReady() {
  return typeof globalThis.AppState !== 'undefined';
}

/**
 * Wraps a legacy global function, preserving the original as `next`.
 *
 * The legacy core is full of hand-rolled versions of this (`const _orig = fn;
 * fn = function () { ... }`), including some that re-wrap on a timer. Funnelling
 * them through one helper means a failed patch degrades to the original
 * behaviour instead of leaving a half-applied wrapper behind.
 *
 * @param {string} name  Global function to wrap.
 * @param {(next: Function, ...args: any[]) => any} wrapper
 * @returns {boolean} Whether the patch was applied.
 */
export function wrapGlobal(name, wrapper) {
  const original = globalThis[name];
  if (typeof original !== 'function') {
    console.warn(`[ironpact] cannot patch "${name}": not a function`);
    return false;
  }

  globalThis[name] = function patched(...args) {
    try {
      return wrapper.call(this, original.bind(this), ...args);
    } catch (err) {
      console.error(`[ironpact] patch for "${name}" failed, falling back`, err);
      return original.apply(this, args);
    }
  };
  return true;
}

/** Shows a toast through the legacy toast system, with a console fallback. */
export function toast(message, type = 'success', duration = 3000) {
  const show = globalThis.showToast;
  if (typeof show === 'function') {
    show(message, type, duration);
  } else {
    console.log(`[ironpact:${type}] ${message}`);
  }
}

/** Navigates the legacy hash router. */
export function navigate(path) {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

/** Current route path, without the leading `#`. */
export function currentRoute() {
  return (window.location.hash || '#/').slice(1) || '/';
}

/**
 * Runs `fn` once the legacy core has rendered a view matching `selector`.
 *
 * The legacy core renders by assigning `innerHTML`, with no lifecycle hook to
 * subscribe to, so observing the DOM is the only join point available. This
 * replaces the scattered `setTimeout(..., 1000)` guesses that used to live in
 * index.html: it fires as soon as the node actually exists and disconnects
 * itself, rather than racing a fixed delay.
 *
 * @param {string} selector
 * @param {(el: Element) => void} fn Called once per newly matched element.
 * @returns {() => void} Disposer.
 */
export function onRendered(selector, fn) {
  const seen = new WeakSet();

  const scan = () => {
    document.querySelectorAll(selector).forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      try {
        fn(el);
      } catch (err) {
        console.error(`[ironpact] onRendered("${selector}") handler failed`, err);
      }
    });
  };

  const observer = new MutationObserver(scan);
  const start = () => {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });

  return () => observer.disconnect();
}

/** Runs `fn` on every hash-route change, and once for the current route. */
export function onRoute(fn) {
  const handler = () => {
    try {
      fn(currentRoute());
    } catch (err) {
      console.error('[ironpact] route handler failed', err);
    }
  };
  window.addEventListener('hashchange', handler);
  handler();
  return () => window.removeEventListener('hashchange', handler);
}
